import { Component, Host, h, Prop, Event, EventEmitter, Method, State, Listen, Element } from '@stencil/core';
import Swiper from 'swiper';
import { ComponentSize } from '../../interface';
import { assertAttributeRequirements, Attributes, hasSlot, inheritAriaAttributes, observeAriaAttributes } from '../../utils';

/**
 * @slot - Modal body.
 * @slot heading - Modal heading slot for html content.
 * @slot footer - Modal footer.
 * @slot primary - Modal primary action button.
 * @slot secondary - Modal secondary action button.
 *
 * @part modal - The component's main modal element.
 * @part panel - The component's panel element.
 * @part header - The component's header element.
 * @part hr - The component's header horizontal rule element.
 * @part footer - The component's footer element.
 */
@Component({
  tag: 'o2-modal',
  styleUrl: 'modal.scss',
  shadow: true,
})
export class O2Modal {
  private hasHeadingSlot: boolean;
  private hasFooterSlot: boolean;
  private hasPrimarySlot: boolean;
  private hasSecondarySlot: boolean;
  private needSwiper: boolean = false;

  private swiperInstance: Swiper;

  /**
   * Instance of MutationObserver
   */
  private mutationObserver: MutationObserver;

  private focusableElements: HTMLElement[] = [];

  @Element() private el: HTMLO2ModalElement;

  @State() open: boolean = false;
  @State() hasScroll: boolean = false;

  /**
   * Inherited attributes from the host element.
   */
  @State() inheritedAttributes: Attributes = {};

  /**
   * Main heading of the modal.
   */
  @Prop() heading?: string;

  /**
   * The modal size.
   * Possible values: `"small"`, `"medium"`, `"large"`.
   */
  @Prop() size?: ComponentSize = 'medium';

  /**
   * If true, it is not possible to close modal with click on overlay.
   */
  @Prop() isBlocking?: boolean = false;

  /**
   * Emitted when the modal is open.
   */
  @Event() o2Show!: EventEmitter<any>;

  /**
   * Emitted when the modal is hidden.
   */
  @Event() o2Hide!: EventEmitter<any>;

  /**
   * Show modal manually.
   */
  @Method()
  async show() {
    if (this.open) {
      return;
    }

    this.open = true;
    this.o2Show.emit();

    this.lockPageScroll();

    if (this.needSwiper) {
      await this.importSwiperAndRegisterEvents();
    }

    this.hasModalBodyScroll();

    setTimeout(() => {
      // focus modal container first
      const modalContainer = this.el.shadowRoot.querySelector('.modal') as HTMLElement;
      modalContainer?.focus();
      // then focus on the first interactive element
      this.focusableElements = this.getFocusableElements();
      this.focusFirstInteractive();
    });
  }

  /**
   * Hide modal manually.
   */
  @Method()
  async hide(initiator?: string) {
    if (!this.open) {
      return;
    }

    this.unlockPageScroll();

    this.open = false;
    this.o2Hide.emit({ initiator: initiator });
  }

  @Listen('keydown', { target: 'document' })
  onKeyDown(e: KeyboardEvent) {
    if (!this.open) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.hide('escape');
    }
    if (e.key === 'Tab') {
      this.trapTab(e);
    }
  }

  private lockPageScroll(): void {
    document.body.classList.add('o2-scroll-lock');
  }

  private unlockPageScroll(): void {
    document.body.classList.remove('o2-scroll-lock');
  }

  private onClickCloseOverlay = () => {
    if (!this.isBlocking) {
      this.hide('overlay');
    }
  };

  private hasModalBodyScroll(): void {
    const modalBody = this.el.shadowRoot.querySelector('.modal__body');

    this.hasScroll = modalBody?.clientHeight < modalBody?.scrollHeight;
  }

  private hasMobileResolution(): boolean {
    const deviceWidth = window.innerWidth;
    return deviceWidth < 576;
  }

  private importSwiperAndRegisterEvents = async () => {
    const { Swiper } = await import('swiper');

    const modalPanel: HTMLElement = this.el.shadowRoot.querySelector('.swiper');

    this.swiperInstance = new Swiper(modalPanel, {
      direction: 'vertical',
      slidesPerView: 'auto',
      centeredSlides: true,
    });

    this.swiperInstance.on('touchMove', event => {
      this.swiperInstance.allowSlideNext = false;

      if (event.touches.diff > 200) {
        this.hide();
      }
    });
  };

  /** Get real 100vh on every device. BUG-41780
   * https://css-tricks.com/the-trick-to-viewport-units-on-mobile
   */
  private getVHUnitSize = () => {
    let vh = window.innerHeight * 0.01;
    this.el.style.setProperty('--vh', `${vh}px`);
  };

  /**
   * Update the swiper state if some external change of slides happened.
   */
  private updateSwiper = () => {
    if (this.swiperInstance) {
      this.swiperInstance.update();
    }
  };

  /**
   * ResizeObserver is checking changes on elements, also clientHeight.
   */
  private observeHeightChange = () => {
    const modalPanel = this.el.shadowRoot.querySelector('.modal__panel');
    const resizeObserver = new ResizeObserver(() => {
      this.hasModalBodyScroll();
      this.updateSwiper();
    });
    resizeObserver.observe(modalPanel);
  };

  /**
   * Collects all focusable elements inside the modal (light & shadow DOM)
   */
  private getFocusableElements(): HTMLElement[] {
    const elements: HTMLElement[] = [];

    const selector =
      'o2-input, o2-checkbox, o2-datetime, o2-icon-button, o2-radio, o2-search, o2-select, o2-select-multiple, o2-select-single, o2-textarea, button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.el.querySelectorAll<HTMLElement>(selector).forEach(el => {
      if (!el.hasAttribute('disabled')) elements.push(el);
    });

    const modalClose: HTMLElement = this.el.shadowRoot.querySelector('.modal__close');
    elements.push(modalClose);

    return elements;
  }

  /**
   * Walks down through any shadowRoots to find the actual focused element
   */
  private getDeepActiveElement(root: Document | ShadowRoot = document): Element | null {
    const hostOrDocActive = root.activeElement as Element | null;
    if (!hostOrDocActive) return null;
    // if this element hosts a shadowRoot, recurse in
    if ((hostOrDocActive as HTMLElement).shadowRoot) {
      return this.getDeepActiveElement((hostOrDocActive as HTMLElement).shadowRoot!);
    }
    return hostOrDocActive;
  }

  /**
   * Internal Tab focus trap logic
   */
  private trapTab(e: KeyboardEvent) {
    const focusables = this.focusableElements as HTMLElement[];
    if (!focusables.length) return;

    // stop the browser’s native Tab behavior
    e.preventDefault();

    // find the “deep” active element
    const active = this.getDeepActiveElement() as HTMLElement | null;
    let idx = active ? focusables.indexOf(active) : -1;

    // if it wasn’t one of ours, start “just outside” the ring:
    // for forward Tab we start at -1 → nextIdx becomes 0
    // for reverse Tab we start at length → nextIdx becomes length-1
    if (idx < 0) {
      idx = e.shiftKey ? focusables.length : -1;
    }

    // move +1 or -1, wrap around
    const direction = e.shiftKey ? -1 : 1;
    const nextIdx = (idx + direction + focusables.length) % focusables.length;

    const next = focusables[nextIdx] as HTMLElement & { setFocus?: () => void };
    if (typeof next.setFocus === 'function') {
      next.setFocus();
    } else {
      next.focus();
    }
  }

  /**
   * Focus the first interactive element inside modal
   */
  private focusFirstInteractive() {
    if (this.focusableElements.length) {
      this.focusableElements[0].focus();
    } else if (this.hasPrimarySlot) {
      (this.el.shadowRoot.querySelector('[slot="primary"]') as HTMLElement)?.focus();
    }
  }

  private setButtonAriaLabels(): void {
    ['footer', 'secondary', 'primary'].forEach(name => {
      const slot = this.el.shadowRoot.querySelector(`slot[name="${name}"]`);
      if (slot) {
        const assigned = (slot as HTMLSlotElement).assignedElements({ flatten: true });
        assigned.forEach(el => {
          if (el.tagName.toLowerCase() === 'o2-button') {
            const btn = el as HTMLElement;
            // Only set if not already provided
            if (!btn.hasAttribute('aria-label')) {
              let label: string;
              switch (name) {
                case 'primary':
                  label = 'Potvrdiť';
                  break;
                case 'secondary':
                  label = 'Zrušiť';
                  break;
              }
              btn.setAttribute('aria-label', label);
            }
          }
        });
      }
    });
  }

  private handleSlotChange = () => {
    this.setButtonAriaLabels();
  }

  connectedCallback() {
    this.hasHeadingSlot = hasSlot(this.el, '[slot="heading"]');
    this.hasFooterSlot = hasSlot(this.el, '[slot="footer"]');
    this.hasPrimarySlot = hasSlot(this.el, '[slot="primary"]');
    this.hasSecondarySlot = hasSlot(this.el, '[slot="secondary"]');
    this.getVHUnitSize();

    this.needSwiper = this.size === 'small' && this.hasMobileResolution();

    const ariaLabelledBy = this.el.getAttribute('aria-labelledby');
    const ariaDescribedBy = this.el.getAttribute('aria-describedby');

    assertAttributeRequirements(
      'o2-modal',
      {
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
      },
      {
        requiredOneOf: ['aria-labelledby', 'aria-describedby'],
      },
    );

    // only compatible place with LFR and all browsers
    this.mutationObserver = observeAriaAttributes(this.el as HTMLElement, () => {
      // merge attributes
      const newAttributes = inheritAriaAttributes(this.el as HTMLElement);
      this.inheritedAttributes = { ...this.inheritedAttributes, ...newAttributes };
    });
  }

  componentDidLoad() {
    const secondaryButton = this.el.querySelector('o2-button[slot="secondary"]');

    if (secondaryButton) {
      secondaryButton.addEventListener('click', event => {
        if (!event.defaultPrevented) {
          this.hide('button');
        }
      });
    }

    this.observeHeightChange();

    this.inheritedAttributes = inheritAriaAttributes(this.el as HTMLElement);
  }

  disconnectedCallback() {
    this.mutationObserver?.disconnect();
  }

  render() {
    const close = !this.isBlocking ? (
      <button type="button" class="modal__close" aria-label="Zatvoriť" onClick={() => this.hide('close')}>
        <o2-icon name="close-circle" solid color="gray-medium" size={24} ></o2-icon>
      </button>
    ) : (
      ''
    );

    return (
      <Host>
        <div
          part="modal"
          class={`${this.needSwiper ? 'swiper' : ''} modal ${this.open ? 'modal--open' : ''} ${this.open ? 'modal--popin' : 'modal--popout'} ${
            this.size ? `modal--${this.size}` : ''
          } ${close !== '' ? 'modal--has-close' : ''}`}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          {...this.inheritedAttributes}
        >
          <div class="modal__overlay" onClick={this.onClickCloseOverlay}></div>
          <div class={`${this.needSwiper ? 'swiper-wrapper' : 'wrapper'}`}>
            <div part="panel" class={`modal__panel ${this.needSwiper ? 'swiper-slide' : ''}`}>
              {this.needSwiper ? <div class="modal__pull"></div> : ''}
              {this.hasHeadingSlot || this.heading ? (
                <header part="header" class="modal__header">
                  <h2>
                    {this.heading ? this.heading : ''}
                    {this.hasHeadingSlot ? <slot name="heading"></slot> : ''}
                  </h2>
                  <hr part="hr" />
                </header>
              ) : (
                ''
              )}

              {close}

              <section part="body" class="modal__body">
                <slot></slot>
              </section>
              {this.hasFooterSlot || this.hasPrimarySlot || this.hasSecondarySlot ? (
                <footer part="footer" class={`modal__footer ${this.hasScroll ? 'modal__footer--shadow' : ''}`}>
                  {this.hasSecondarySlot ? <slot name="secondary" onSlotchange={this.handleSlotChange}></slot> : ''}
                  {this.hasPrimarySlot ? <slot name="primary" onSlotchange={this.handleSlotChange}></slot> : ''}
                  {this.hasFooterSlot ? <slot name="footer" onSlotchange={this.handleSlotChange}></slot> : ''}
                </footer>
              ) : (
                ''
              )}
            </div>

            {this.needSwiper ? <div class="swiper-slide swiper-slide--fake"></div> : ''}
          </div>
        </div>
      </Host>
    );
  }
}
